import { supabase } from "./supabase";
// --- Constants ----------------------------------------------------------------
const MODES = [
  { key: "rental",    label: "Rental",     sub: "Buy & Hold",      icon: "\u{1F3E0}", color: "#22c55e", bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.3)"  },
  { key: "wholesale", label: "Wholesale",  sub: "MAO Calculator",  icon: "\u{1F4CB}", color: "#2563eb", bg: "rgba(37,99,235,0.12)",  border: "rgba(37,99,235,0.3)"  },
  { key: "flip",      label: "Fix & Flip", sub: "Profit & ROI",    icon: "\u{1F528}", color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.3)" },
  { key: "brrrr",     label: "BRRRR",      sub: "Refi & Recycle",  icon: "\u267B\uFE0F", color: "#7c3aed", bg: "rgba(124,58,237,0.12)", border: "rgba(124,58,237,0.3)" },
  { key: "subto",     label: "Subject-To", sub: "Loan Takeover",   icon: "\u{1F511}", color: "#0891b2", bg: "rgba(8,145,178,0.12)",  border: "rgba(8,145,178,0.3)"  },
  { key: "novation",  label: "Novation",   sub: "Contract Assign", icon: "\u{1F4DD}", color: "#be185d", bg: "rgba(190,24,93,0.12)",  border: "rgba(190,24,93,0.3)"  },
  { key: "househack", label: "House Hack", sub: "Live & Build",    icon: "\u{1F3E1}", color: "#d97706", bg: "rgba(217,119,6,0.12)",  border: "rgba(217,119,6,0.3)"  },
];

// ── Stripe Price IDs ─────────────────────────────────────────────────────────
const STRIPE_PRICES = {
  "pro-monthly":      "price_1T37yNCFJ1jylHcuPsQb2wbW",  // Pro $20/mo
  "pro-annual":       "price_1TDcIVCFJ1jylHcuHMumLqd7",  // Pro $15/mo ($180/yr)
  "investor-monthly": "price_1TEuZjCFJ1jylHcu0dCjEWJp",  // Investor $39/mo
  "investor-annual":  "price_1TEuaHCFJ1jylHcuXUSKCeM7",  // Investor $31/mo ($372/yr)
  // Legacy aliases
  "annual":           "price_1TDcIVCFJ1jylHcuHMumLqd7",
  "monthly":          "price_1T37yNCFJ1jylHcuPsQb2wbW",
};
const getStripePrice = (billing) => STRIPE_PRICES[billing] || STRIPE_PRICES["pro-annual"];

// ── Property Lookup Cache ─────────────────────────────────────────────────────
// Two-layer cache: memory (instant, per-session) + localStorage (permanent until user clears)
// Keyed by address — one lookup serves all 7 calculators for the same property

const CACHE_KEY = "ds_lookup_cache_v2";
const CACHE_MAX = 100; // max addresses stored (evict oldest beyond this)

const _lookupCache = {}; // memory layer

// Load persisted cache into memory on startup
(()=>{
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw);
    const now = Date.now();
    const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
    Object.entries(stored).forEach(([k, entry]) => {
      // Skip stale entries (older than 30 days) — comps and AVM change over time
      if(entry.ts && (now - entry.ts) > MAX_AGE_MS) return;
      _lookupCache[k] = entry.data || entry;
    });
  } catch(e) {}
})();

// Save a single entry to localStorage
const _persistEntry = (key, data) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const stored = raw ? JSON.parse(raw) : {};
    stored[key] = { data, ts: Date.now() };
    // Evict oldest if over limit
    const keys = Object.keys(stored);
    if (keys.length > CACHE_MAX) {
      keys.sort((a,b) => (stored[a].ts||0) - (stored[b].ts||0));
      keys.slice(0, keys.length - CACHE_MAX).forEach(k => delete stored[k]);
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(stored));
  } catch(e) {}
};

const getLookupCached = async (address, mode, userId, isPro=false, isInvestor=false) => {
  const key = address.trim().toLowerCase();
  if (_lookupCache[key]) {
    const cached = _lookupCache[key];
    const hasComps = (cached.rentalComps||[]).length>0 || (cached.salesComps||[]).length>0;
    const hasValue = cached.value || cached.rent || cached.property?.squareFootage;
    if (hasComps || hasValue) {
      if(process.env.NODE_ENV==='development') console.log(`[cache] HIT — ${key}`);
      return cached;
    }
    delete _lookupCache[key];
  }
  const params = new URLSearchParams({ address, mode });
  if (userId)     params.append("userId",     userId);
  if (isPro)      params.append("isPro",      "true");
  if (isInvestor) params.append("isInvestor", "true");
  const res  = await fetch(`/api/lookup-property?${params}`, { headers: { Authorization: `Bearer ${supabase._token || ""}` } });
  if(!res.ok){
    const errText = await res.text().catch(()=>'Network error');
    return {error: `api_error_${res.status}`, message: errText.slice(0,200)};
  }
  const data = await res.json();
  if (!data.error && data.error !== "lookup_limit_reached") {
    // Strip usage fields before caching (they're per-request, not per-address)
    const {_used, _limit, ...cacheData} = data;
    _lookupCache[key] = cacheData;   // memory layer
    _persistEntry(key, cacheData);   // localStorage layer (permanent)
    if(process.env.NODE_ENV==='development') console.log(`[cache] STORED — ${key}`);
  }
  return data;
};

// Module-level user reference for non-React tracking (ProGate, upgrade prompts)
let _currentUserId = null;
const setCurrentUserId = (id) => { _currentUserId = id; };
const getCurrentUserId = () => _currentUserId;
// Safe localStorage write — catches QuotaExceededError and warns once
let _lsQuotaWarned = false;
const lsSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch(e) {
    if((e.name==='QuotaExceededError'||e.code===22||e.code===1014) && !_lsQuotaWarned) {
      _lsQuotaWarned = true;
      console.warn('[DealSource] localStorage full — some data may not persist offline. Clear browser storage or use a different browser.');
    }
  }
};
// Safe redirect — only allows Stripe and Supabase domains to prevent open redirect attacks
const safeRedirect = (url) => {
  try {
    const parsed = new URL(url);
    const allowed = ['stripe.com','checkout.stripe.com','supabase.co','accounts.google.com'];
    if(allowed.some(d => parsed.hostname.endsWith(d))) {
      window.location.href = url;
    } else {
      console.warn('[security] Blocked redirect to unexpected domain:', parsed.hostname);
    }
  } catch { console.warn('[security] Invalid redirect URL:', url); }
};

// Analytics stub — wire to PostHog / Mixpanel / Amplitude when ready
// Called on: save_deal, save_limit_hit, upgrade_prompt
const trackEvent = (userId, event, props={}) => {
  try {
    if(process.env.NODE_ENV==='development') console.log('[track]', event, {userId, ...props});
    // window.posthog?.capture(event, {distinct_id: userId, ...props});
  } catch {}
};
const INVESTOR_TYPES = ["Buy & Hold Investor","Fix & Flip Investor","Wholesaler","BRRRR Investor","Short-Term Rental","Commercial Investor","Beginner / Learning"];

const MEDALS = [
  { id: "bronze",   label: "Bronze",   icon: "", min: 0,        color: "#b45309", bg: "#fef3c7", desc: "Getting started" },
  { id: "silver",   label: "Silver",   icon: "", min: 500000,   color: "#94a3b8", bg: "#F1F5F9", desc: "Building momentum" },
  { id: "gold",     label: "Gold",     icon: "", min: 2000000,  color: "#f59e0b", bg: "rgba(251,191,36,0.08)", desc: "Serious investor" },
  { id: "platinum", label: "Platinum", icon: "", min: 5000000,  color: "#7c3aed", bg: "rgba(124,58,237,0.12)", desc: "Elite portfolio" },
  { id: "diamond",  label: "Diamond",  icon: "", min: 10000000, color: "#0891b2", bg: "rgba(8,145,178,0.12)", desc: "Top 1% investor" },
];
const getMedal = (v) => [...MEDALS].reverse().find(m => v >= m.min) || MEDALS[0];
const fmtD = (n) => n == null || isNaN(n) || !isFinite(n) ? " - " : (!(n>=0) ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString();
const fmtP = (n) => (isNaN(n) || !isFinite(n)) ? " - " : `${(n*100).toFixed(1)}%`;
const fmtM = (n) => { if (!n || !isFinite(n)) return "$0"; if (n >= 1000000) return `$${(n/1000000).toFixed(1)}M`; if (n >= 1000) return `$${(n/1000).toFixed(0)}K`; return `$${Math.round(n)}`; };

export {
  MODES,
  STRIPE_PRICES, getStripePrice,
  CACHE_KEY, CACHE_MAX, _lookupCache, _persistEntry, getLookupCached,
  _currentUserId, setCurrentUserId, getCurrentUserId, lsSet,
  safeRedirect,
  trackEvent,
  INVESTOR_TYPES,
  MEDALS, getMedal,
  fmtD, fmtP, fmtM,
};
